import { evaluateAuthority, type AuthorityRule } from "@/lovable-demo/lib/governance/authority";
import type { AppRole } from "@/lovable-demo/lib/governance/types";
import type { Rpc } from "./pfmea-signoff";
import type { ClauseMapping, ProblemRecord } from "./types";

/**
 * Package #2.7 — authored, governed Standard Compliance verdicts.
 *
 * Compliant / Gap: Quality Engineer or Quality Director (compliance.verdict).
 * Violated, or changing away from Violated: Quality Director only
 * (compliance.verdict_violated). The database function
 * `compliance_set_verdict` and its insert trigger enforce the same rule; this
 * module mirrors it so the UI can explain what is allowed.
 */

export type Verdict = ClauseMapping["verdict"];
export const VERDICTS: Verdict[] = ["Compliant", "Gap", "Violated"];

export const VERDICT_ROLES: AppRole[] = ["quality_engineer", "quality_director"];
export const VIOLATED_ROLES: AppRole[] = ["quality_director"];

export const SEEDED_ASSESSOR = "Initial assessment";
export const SEEDED_ASSESSOR_TITLE = "";
export const SEEDED_VERDICT_LABEL = "Initial assessment";

export function verdictRuleKey(oldVerdict: Verdict, newVerdict: Verdict) {
  return oldVerdict === "Violated" || newVerdict === "Violated"
    ? "compliance.verdict_violated"
    : "compliance.verdict";
}

export function rolesForVerdictChange(oldVerdict: Verdict, newVerdict: Verdict): AppRole[] {
  return verdictRuleKey(oldVerdict, newVerdict) === "compliance.verdict_violated"
    ? VIOLATED_ROLES
    : VERDICT_ROLES;
}

export function canChangeVerdict(roles: AppRole[], oldVerdict: Verdict, newVerdict: Verdict) {
  return roles.some((r) => rolesForVerdictChange(oldVerdict, newVerdict).includes(r));
}

export function validateVerdictChange(
  clause: ClauseMapping | undefined,
  newVerdict: Verdict,
  rationale: string,
): string | null {
  if (!clause) return "Clause not found.";
  if (!VERDICTS.includes(newVerdict)) return "Choose a verdict.";
  if (clause.verdict === newVerdict) return "The new verdict is the same as the current verdict.";
  if (!rationale.trim()) return "A rationale is required for a verdict change.";
  if (rationale.trim().length < 10) return "The rationale must be at least 10 characters.";
  return null;
}

/** Who assessed the current verdict. Seeded verdicts get a clearly synthetic assessor. */
export function verdictAuthorship(clause: ClauseMapping, record: Pick<ProblemRecord, "openedAt">) {
  if (clause.assessedBy) {
    return {
      seeded: false,
      by: clause.assessedBy,
      title: clause.assessedByTitle ?? "",
      at: clause.assessedAt ?? "",
      rule: clause.authorityRuleKey ?? "",
    };
  }
  return {
    seeded: true,
    by: SEEDED_ASSESSOR,
    title: SEEDED_ASSESSOR_TITLE,
    at: record.openedAt,
    rule: "Initial record value",
  };
}

export interface VerdictEvidence {
  kind?: "file" | "reference";
  fileName?: string;
  filePath?: string;
  reference?: string;
  description?: string;
}

export interface VerdictChange {
  id: string;
  recordId: string;
  clauseId: string;
  oldVerdict: Verdict;
  newVerdict: Verdict;
  rationale: string;
  evidence: VerdictEvidence;
  assessedBy: string;
  assessedByTitle: string;
  authorityRuleKey: string;
  at: string;
}

export function verdictChangeFromRow(r: {
  id: string;
  record_id: string;
  clause_id: string;
  old_verdict: string;
  new_verdict: string;
  rationale: string;
  evidence: unknown;
  assessed_by_label: string;
  assessed_by_title: string;
  authority_rule_key: string;
  created_at: string;
}): VerdictChange {
  return {
    id: r.id,
    recordId: r.record_id,
    clauseId: r.clause_id,
    oldVerdict: r.old_verdict as Verdict,
    newVerdict: r.new_verdict as Verdict,
    rationale: r.rationale,
    evidence: (r.evidence ?? {}) as VerdictEvidence,
    assessedBy: r.assessed_by_label,
    assessedByTitle: r.assessed_by_title,
    authorityRuleKey: r.authority_rule_key,
    at: r.created_at,
  };
}

export type VerdictResult = { ok: true } | { ok: false; error: string };

/** Class A — authority check, then one transactional DB call (audit + history + record). */
export async function submitVerdictChange(
  rpc: Rpc,
  ctx: {
    record: ProblemRecord;
    clauseId: string;
    newVerdict: Verdict;
    rationale: string;
    evidence?: VerdictEvidence | null;
    roles: AppRole[];
    rules: AuthorityRule[];
  },
): Promise<VerdictResult> {
  const clause = ctx.record.clauses.find((c) => c.id === ctx.clauseId);
  const invalid = validateVerdictChange(clause, ctx.newVerdict, ctx.rationale);
  if (invalid || !clause) return { ok: false, error: invalid ?? "Clause not found." };
  const decisionType = verdictRuleKey(clause.verdict, ctx.newVerdict);
  const verdict = evaluateAuthority({
    decisionType,
    record: ctx.record,
    roles: ctx.roles,
    rules: ctx.rules,
    exactRoles: rolesForVerdictChange(clause.verdict, ctx.newVerdict),
  });
  if (!verdict.authorised)
    return {
      ok: false,
      error:
        decisionType === "compliance.verdict_violated"
          ? "Only a Quality Director can set or clear a Violated verdict. Nothing was recorded."
          : "Only a Quality Engineer or Quality Director can change a compliance verdict. Nothing was recorded.",
    };
  const { error } = await rpc("compliance_set_verdict", {
    p_record_id: ctx.record.id,
    p_clause_id: ctx.clauseId,
    p_new_verdict: ctx.newVerdict,
    p_rationale: ctx.rationale.trim(),
    p_evidence: ctx.evidence ?? {},
  });
  if (error) return { ok: false, error: `Nothing was recorded. ${error.message}` };
  return { ok: true };
}
